import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/* ============================================================
   COLORS
   ============================================================ */

const PAGE_BG = "var(--user-bg, #E8D9D5)";
const MAROON = "var(--user-maroon, #2B0000)";
const GOLD = "var(--user-gold, #D4AF37)";

export default function HomeExperience() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const cardsRef = useRef([]);
  const navigate = useNavigate();

  /* ============================================================
     CARD DATA
     ============================================================ */

  const cards = [
    {
      id: 1,
      title: "Blogs",
      image:
        "https://images.unsplash.com/photo-1602173574767-37ac01994b2a?w=1200&h=1000&fit=crop&crop=center&auto=format",
      path: "/blogs",
    },

    {
      id: 2,
      title: "Contact Us",
      image:
        "https://images.unsplash.com/photo-1583394838336-acd977736f90?w=1200&h=1000&fit=crop&crop=center&auto=format",
      path: "/contact",
    },

    {
      id: 3,
      title: "Visit Our Store",
      image:
        "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&h=1000&fit=crop&crop=center&auto=format",
      path: "/contact",
    },

    {
      id: 4,
      title: "Our Collections",
      image:
        "https://images.unsplash.com/photo-1617038220319-276d3cfab638?w=1200&h=1000&fit=crop&crop=center&auto=format",
      path: "/all-products",
    },

    {
      id: 5,
      title: "Teams",
      image:
        "https://images.unsplash.com/photo-1603201667141-5a2d4c673378?q=80&w=1196&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      path: "/teams",
    },

    {
      id: 6,
      title: "About SL Gold",
      image:
        "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=1200&h=1000&fit=crop&crop=center&auto=format",
      path: "/about-us",
    },
  ];

  /* ============================================================
     SCROLL ANIMATION
     ============================================================ */

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      /* HEADING ENTRANCE */

      gsap.fromTo(
        headingRef.current,
        {
          opacity: 0,
          y: 20,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power3.out",

          scrollTrigger: {
            trigger: section,
            start: "top 85%",
            once: true,
          },
        }
      );

      /* CARD ENTRANCE */

      gsap.fromTo(
        cardsRef.current,
        {
          opacity: 0,
          y: 25,
        },
        {
          opacity: 1,
          y: 0,

          duration: 0.75,
          stagger: 0.07,
          ease: "power3.out",

          scrollTrigger: {
            trigger: section,
            start: "top 78%",
            once: true,
          },
        }
      );
    }, section);

    return () => ctx.revert();
  }, []);

  /* ============================================================
     NAVIGATION
     ============================================================ */

  const handleNavigate = (path) => {
    navigate(path);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <section
      ref={sectionRef}
      className="
        relative
        w-full
        overflow-hidden

        px-3
        py-12

        sm:px-5
        sm:py-14

        md:px-8
        md:py-14

        lg:px-12
        lg:py-16

        xl:px-16
      "
      style={{
        backgroundColor: PAGE_BG,
      }}
    >
      {/* ========================================================
          MAIN CONTAINER
          15% NARROWER ON LAPTOP/DESKTOP
          ======================================================== */}

      <div
        className="
          relative
          mx-auto

          w-full

          md:w-[85%]

          max-w-[1610px]

          md:overflow-hidden
          md:rounded-[16px]
          md:border
        "
        style={{
          borderColor: "rgba(43, 0, 0, 0.18)",
        }}
      >
        {/* ======================================================
            HEADING
            ====================================================== */}

        <div
          ref={headingRef}
          className="
            px-5
            pb-8
            pt-2
            text-center

            sm:px-8
            sm:pb-9

            md:px-10
            md:pb-9
            md:pt-9

            lg:pb-10
            lg:pt-10
          "
        >
          {/* ====================================================
              DISCOVER SL GOLD
              ==================================================== */}

          <div
            className="
              mb-3
              flex
              items-center
              justify-center
              gap-3
            "
          >
            <span
              className="
                h-px
                w-7

                sm:w-9
              "
              style={{
                backgroundColor: MAROON,
                opacity: 0.55,
              }}
            />

            <span
              className="
                text-[9px]
                font-semibold
                uppercase
                tracking-[0.3em]

                sm:text-[10px]
              "
              style={{
                color: MAROON,
              }}
            >
              Discover SL Gold
            </span>

            <span
              className="
                h-px
                w-7

                sm:w-9
              "
              style={{
                backgroundColor: MAROON,
                opacity: 0.55,
              }}
            />
          </div>

          {/* ====================================================
              MAIN TITLE
              ==================================================== */}

          <h2
            className="
              font-serif

              text-[34px]
              font-medium
              leading-[1.05]
              tracking-[-0.025em]

              sm:text-[39px]

              md:text-[44px]

              lg:text-[48px]
            "
            style={{
              color: MAROON,
            }}
          >
            Experience SL Gold
          </h2>

          {/* ====================================================
              SUBTITLE
              ==================================================== */}

          <p
            className="
              mx-auto
              mt-2
              max-w-[680px]

              font-serif
              text-[15px]
              leading-[1.5]

              sm:text-[17px]

              md:text-[18px]
            "
            style={{
              color: MAROON,
              opacity: 0.68,
            }}
          >
            Explore our world of jewellery, craftsmanship and timeless
            elegance.
          </p>
        </div>

        {/* ======================================================
            CARDS GRID
            ====================================================== */}

        <div
          className="
            grid
            grid-cols-1
            gap-2

            md:grid-cols-3
            md:gap-0
          "
        >
          {cards.map((card, index) => (
            <button
              key={card.id}
              ref={(element) => {
                cardsRef.current[index] = element;
              }}
              type="button"
              onClick={() => handleNavigate(card.path)}
              className={`
                relative
                block
                w-full
                overflow-hidden

                cursor-pointer
                text-left
                outline-none

                rounded-[13px]
                border

                md:rounded-none
                md:border-l-0

                ${
                  index < 3
                    ? "md:border-t md:border-b-0"
                    : "md:border-t md:border-b-0"
                }

                ${
                  index === 2 || index === 5
                    ? "md:border-r-0"
                    : "md:border-r"
                }
              `}
              style={{
                borderColor: "rgba(212, 175, 55, 0.20)",
                backgroundColor: MAROON,
              }}
            >
              {/* =================================================
                  IMAGE
                  ================================================= */}

              <div
                className="
                  relative
                  aspect-[1/0.94]
                  w-full
                  overflow-hidden

                  md:aspect-auto
                  md:h-[260px]

                  lg:h-[300px]

                  xl:h-[335px]
                "
              >
                <img
                  src={card.image}
                  alt={card.title}
                  loading="lazy"
                  className="
                    h-full
                    w-full
                    object-cover
                    object-center
                  "
                  onError={(e) => {
                    e.currentTarget.src = `https://via.placeholder.com/1000x900/E8D9D5/2B0000?text=${encodeURIComponent(
                      card.title
                    )}`;
                  }}
                />
              </div>

              {/* =================================================
                  DARK MAROON TITLE AREA
                  ================================================= */}

              <div
                className="
                  relative

                  flex
                  min-h-[70px]
                  items-center
                  justify-between

                  border-t

                  px-6
                  py-4

                  sm:min-h-[74px]
                  sm:px-7

                  md:min-h-[58px]
                  md:justify-center
                  md:px-5
                  md:py-3

                  lg:min-h-[62px]
                "
                style={{
                  backgroundColor: MAROON,
                  borderColor: "rgba(212,175,55,0.28)",
                }}
              >
                {/* GOLD TITLE */}

                <h3
                  className="
                    font-serif

                    text-[20px]
                    font-medium
                    leading-none

                    sm:text-[21px]

                    md:text-center
                    md:text-[17px]
                    md:uppercase

                    lg:text-[18px]
                  "
                  style={{
                    color: GOLD,
                  }}
                >
                  {card.title}
                </h3>

                {/* MOBILE ARROW */}

                <ArrowRight
                  strokeWidth={1.3}
                  className="
                    h-7
                    w-7
                    shrink-0

                    md:hidden
                  "
                  style={{
                    color: GOLD,
                  }}
                />

                {/* STATIC GOLD LINE */}

                <span
                  className="
                    absolute
                    bottom-[8px]
                    left-1/2

                    hidden

                    h-px
                    w-[20px]

                    -translate-x-1/2

                    md:block
                  "
                  style={{
                    backgroundColor: GOLD,
                    opacity: 0.7,
                  }}
                />
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}